import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-glossary',
    category: 'results',
    title: 'Slovník — energetické pojmy v běžném jazyce',
    summary: 'Každý pojem, který se objevuje v grafech výsledků a kartách KPI, na jednom místě.',
    body: (
      <>
        <p>Ostatní články v této sekci odkazují zpět sem. Uložte si tuto stránku jako rychlý přehled.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Pojem</th><th>Co znamená</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>kWh</strong> (kilowatthodina)</td>
              <td>Jednotka <em>energie</em>. Jedna kWh ≈ přímotopný ventilátor běžící jednu hodinu. Účty jsou v kWh.</td>
            </tr>
            <tr>
              <td><strong>kW</strong> (kilowatt)</td>
              <td>Jednotka <em>výkonu</em> — jak rychle se energie právě spotřebovává. Varná konvice 2 kW po půl hodině spotřebuje 1 kWh.</td>
            </tr>
            <tr>
              <td><strong>Špičková poptávka</strong></td>
              <td>Nejvyšší výkon (kW) odebíraný v daném období. Poplatky za síť jsou s tímto číslem často svázány.</td>
            </tr>
            <tr>
              <td><strong>Výroba</strong></td>
              <td>Elektřina vyrobená vašimi solárními panely, větrnou turbínou apod.</td>
            </tr>
            <tr>
              <td><strong>Zatížení / spotřeba</strong></td>
              <td>Elektřina spotřebovaná budovami a zařízeními.</td>
            </tr>
            <tr>
              <td><strong>Vlastní spotřeba (%)</strong></td>
              <td>Z veškeré energie, kterou jste <em>vyrobili</em>, jaký podíl jste spotřebovali sami
                (místo exportu). Vysoká hodnota = méně plýtváte svou výrobou.</td>
            </tr>
            <tr>
              <td><strong>Soběstačnost (%)</strong></td>
              <td>Z veškeré energie, kterou jste <em>spotřebovali</em>, jaký podíl pocházel z vaší vlastní výroby.
                Vysoká hodnota = méně závislosti na síti.</td>
            </tr>
            <tr>
              <td><strong>Sdílená energie</strong> <em>(koncept)</em></td>
              <td>Energie vyrobená jedním členem společenství a spotřebovaná ve stejné hodině jiným
                členem. Mnoho národních schémat toto odměňuje. Platforma momentálně
                nezobrazuje toto jako samostatný KPI — projevuje se nepřímo prostřednictvím vlastní spotřeby a
                grafu Energetická bilance.</td>
            </tr>
            <tr>
              <td><strong>Odběr / dodávka</strong></td>
              <td>Odběr = elektřina odebraná ze sítě. Dodávka = elektřina dodaná do sítě.</td>
            </tr>
            <tr>
              <td><strong>Tarif</strong></td>
              <td>Cena elektřiny v €/kWh, která se často mění po hodinách.</td>
            </tr>
            <tr>
              <td><strong>Stav nabití (SOC)</strong></td>
              <td>Jak je baterie nabitá, 0 % (prázdná) až 100 % (plná).</td>
            </tr>
            <tr>
              <td><strong>NPV</strong> (čistá současná hodnota)</td>
              <td>Celková hodnota investice po dobu její životnosti vyjádřená v dnešních penězích. <strong>Kladná = zisková</strong>; záporná = ztráta.</td>
            </tr>
            <tr>
              <td><strong>IRR</strong> (vnitřní míra výnosu) <em>(koncept)</em></td>
              <td>Roční procentuální výnos z investice. Výsledky CBA platformy
                aktuálně zobrazují NPV, BCR, dobu návratnosti, roční úspory a CO₂ — nikoli přímo IRR —
                tento pojem se ale v popisu nástroje vyskytuje.</td>
            </tr>
            <tr>
              <td><strong>BCR</strong> (poměr přínosů a nákladů)</td>
              <td>Celkové přínosy vydělené celkovými náklady. Nad 1 = investice se vrátí.</td>
            </tr>
            <tr>
              <td><strong>Doba návratnosti (roky)</strong></td>
              <td>Kolik let trvají úspory, než pokryjí počáteční náklady.</td>
            </tr>
            <tr>
              <td><strong>MAPE</strong> (střední absolutní procentuální chyba)</td>
              <td>Chyba prognózy. Nižší je lepší — např. MAPE 10 % znamená, že předpovědi se průměrně odchylují o
                10 %.</td>
            </tr>
            <tr>
              <td><strong>GHI</strong></td>
              <td>Globální horizontální ozáření — celkové sluneční záření dopadající na vodorovný povrch v W/m². Hlavní meteorologický vstup pro solární prognózování.</td>
            </tr>
          </tbody>
        </Table>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-forecast',
    category: 'results',
    title: 'Jak číst graf prognózy',
    summary: 'Co znamená čára, stínované pásmo a číslo přesnosti.',
    body: (
      <>
        <p>
          Výsledek prognózy má tři části: <strong>historická linie</strong>,
          {' '}<strong>predikovaná linie</strong> a <strong>stínované pásmo</strong> kolem
          predikce zobrazující rozsah nejistoty.
        </p>
        <ScreenshotPlaceholder
          img="forecast-results.png"
          description="A finished forecast — line for past data, a forecast line, and a shaded band around the prediction. A MAPE value and a Confidence level badge (LOW / MEDIUM / MEDIUM-HIGH / HIGH) are shown alongside."
          source="Platform — Forecasting page, after a successful run. Capture the result panel on the right: chart + the MAPE/Confidence indicators. (A community with uploaded load data is needed.)"
          caption="Typický výsledek prognózy."
        />

        <ul>
          <li><strong>Historická linie</strong> — co se skutečně stalo. Použijte ji ke kontrole, zda vstupní data vypadají správně.</li>
          <li><strong>Predikovaná linie</strong> — nejlepší jednotlivý odhad modelu pro budoucnost.</li>
          <li><strong>Stínované pásmo</strong> — rozsah, do kterého skutečná hodnota pravděpodobně spadne. <em>Úzké</em> pásmo znamená, že model je přesný; <em>široké</em> pásmo znamená nejistotu (to bývá při dlouhých horizontech nebo s malým množstvím vstupních dat).</li>
        </ul>

        <h5 className="mt-3">Dva ukazatele přesnosti</h5>
        <p>
          Platforma zobrazuje u každé prognózy dva souhrnné ukazatele — čtěte je společně:
        </p>

        <p className="mb-1"><strong>1. Úroveň spolehlivosti</strong> — jeden odznak s jednou ze čtyř hodnot:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Odznak</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>HIGH</strong></td><td>Dostatek čistých vstupních dat; výsledky vhodné pro provozní rozhodování.</td></tr>
            <tr><td><strong>MEDIUM-HIGH</strong></td><td>Dobré. Použitelné pro plánování.</td></tr>
            <tr><td><strong>MEDIUM</strong></td><td>Použitelné, ale nedokonalé vstupní data — před jednáním v krajních případech ověřte.</td></tr>
            <tr><td><strong>LOW</strong></td><td>Omezená vstupní data nebo slabý signál. Berte prognózu jako orientační; přidejte více historických dat.</td></tr>
          </tbody>
        </Table>

        <p className="mt-3 mb-1"><strong>2. MAPE</strong> (střední absolutní procentuální chyba) — průměrná odchylka mezi predikcí a realitou v %. Přibližné průmyslové měřítko:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>MAPE</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td>&lt; 10%</td><td>Velmi dobré.</td></tr>
            <tr><td>10 – 20%</td><td>Dobré. Použitelné pro plánování a optimalizaci.</td></tr>
            <tr><td>20 – 35%</td><td>Přijatelné pro počáteční fázi. Přidejte data nebo meteorologické informace ke zlepšení.</td></tr>
            <tr><td>&gt; 35%</td><td>Berte jako ilustrativní. Viz <ArticleLink to="tb-forecast-flat">„Moje prognóza vypadá plochá nebo špatná"</ArticleLink>.</td></tr>
          </tbody>
        </Table>

        <Tip>
          Solární prognózy jsou zpravidla přesnější než prognózy zatížení, protože sluneční svit je předvídatelnější. Přidání meteorologického souboru dále snižuje solární MAPE — viz
          {' '}<ArticleLink to="data-weather">Meteorologické soubory</ArticleLink>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-optimisation-kpis',
    category: 'results',
    title: 'Jak číst KPI optimalizace',
    summary: 'Co znamená každá karta v souhrnu KPI po spuštění optimalizace.',
    body: (
      <>
        <p>
          Po spuštění optimalizace se nad grafy zobrazí <strong>souhrn KPI</strong>. Zobrazené
          karty závisí na tom, zda vaše společenství má baterii a zda byl k dispozici tarif,
          ale standardní sada je níže.
        </p>
        <ScreenshotPlaceholder
          img="kpi-cards.png"
          description="The KPI Summary panel that appears after running an optimisation — cards for Daily Cost, Self-Sufficiency, Self-Consumption, Grid Import, Grid Import Cost, Grid Export Revenue, Peak Import and CO2 Avoided."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Capture the KPI Summary row that appears above the charts."
          caption="Panel souhrnu KPI z výsledku optimalizace."
        />

        <h5 className="mt-3">Každý KPI vysvětlen</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>KPI</th><th>Co vám říká</th><th>Jak ho zlepšit</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Denní náklady</strong></td>
              <td>Celkové náklady na provoz společenství za optimalizovaný den (odběry minus výnos z dodávky).</td>
              <td>Lepší tarifní signály, více FV, baterie dimenzovaná pro špičkové hodiny.</td>
            </tr>
            <tr>
              <td><strong>Soběstačnost (%)</strong></td>
              <td>„Z energie, kterou jsem spotřeboval, kolik pocházelo z mé vlastní výroby?"</td>
              <td>Přidejte více FV; snižte spotřebu; přidejte akumulaci.</td>
            </tr>
            <tr>
              <td><strong>Vlastní spotřeba (%)</strong></td>
              <td>„Z energie, kterou jsem vyrobil, kolik jsem spotřeboval sám místo exportu?"</td>
              <td>Přidejte baterii; přesuňte flexibilní zatížení do slunečních hodin.</td>
            </tr>
            <tr>
              <td><strong>Odběr ze sítě (kWh)</strong></td>
              <td>Celková energie zakoupená ze sítě daný den.</td>
              <td>Více FV a akumulace; flexibilní zatížení ve slunečních hodinách.</td>
            </tr>
            <tr>
              <td><strong>Náklady na odběr ze sítě</strong></td>
              <td>Co odběry stojí v měně. Kombinuje objem × tarif.</td>
              <td>Vyhýbejte se odběrům ve špičkových hodinách; předem nabijte z levnějších hodin.</td>
            </tr>
            <tr>
              <td><strong>Výnos z dodávky do sítě</strong></td>
              <td>Co vám bylo zaplaceno za dodávky.</td>
              <td>Závisí na vašem schématu exportního tarifu; není samo o sobě cílem, pokud je preferována vlastní spotřeba.</td>
            </tr>
            <tr>
              <td><strong>Špičkový odběr (kW)</strong></td>
              <td>Nejvyšší hodinový odběr ze sítě během dne. S tímto číslem jsou poplatky za síť často svázány.</td>
              <td>Rozložte zatížení v čase; použijte baterii pro omezení špiček.</td>
            </tr>
            <tr>
              <td><strong>Ušetřené CO2 (kg)</strong></td>
              <td>Emise ušetřené oproti odebrání stejné energie ze sítě. Řídí se vlastní spotřebou FV/větru.</td>
              <td>Maximalizujte vlastní spotřebu; dimenzujte FV na denní zatížení.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Vlastní spotřeba vs. Soběstačnost — nejčastěji zaměňovaná dvojice</h5>
        <p>Znějí podobně, ale odpovídají na opačné otázky:</p>
        <ul>
          <li><strong>Vlastní spotřeba</strong> se týká <em>vaší výroby</em>: kolik z toho, co jste vyrobili, jste spotřebovali?</li>
          <li><strong>Soběstačnost</strong> se týká <em>vaší spotřeby</em>: kolik z toho, co jste spotřebovali, jste sami vyrobili?</li>
        </ul>

        <h5 className="mt-3">Rozumné kontrolní rozsahy</h5>
        <ul>
          <li>Společenství pouze s mírným střešním FV a bez baterie: vlastní spotřeba 60–90 %, soběstačnost 10–25 %.</li>
          <li>Společenství s velkou solární elektrárnou a baterií: soběstačnost může přesáhnout 50 %, vlastní spotřeba 80–95 %.</li>
          <li>Soběstačnost nad ~90 % znamená, že téměř neodebíráte — ověřte, zda soubor se zatížením není podhodnocen.</li>
        </ul>
        <Warn>
          Pokud KPI vypadá nerealisticky dobře (např. 99% soběstačnost při skromném FV), viz
          {' '}<ArticleLink to="tb-kpi-sanity-check">„Moje KPI vypadají příliš dobře nebo příliš špatně"</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
